import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGuestAuth } from '../contexts/GuestAuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FavoriteButton from '../components/FavoriteButton';
import { Heart, Bed, Bath, Users, MapPin, Loader2, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * User's Saved Properties / Favorites Page
 */
const Favorites = () => {
  const { user, isAuthenticated, loading: authLoading, loginWithGoogle } = useGuestAuth();
  const [favorites, setFavorites] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/favorites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
        setProperties(data.properties || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 py-20">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="h-10 w-10 text-slate-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Sign In to View Favorites</h1>
          <p className="text-slate-600 mb-8">
            Create an account or sign in to save your favorite properties and access them from any device.
          </p>
          <Button 
            onClick={loginWithGoogle}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            size="lg"
          >
            <LogIn className="h-5 w-5 mr-2" />
            Sign In with Google
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="h-8 w-8 text-red-500 fill-red-500" />
            <h1 className="text-3xl font-bold text-slate-900">My Favorites</h1>
          </div>
          <p className="text-slate-600">
            {properties.length > 0 
              ? `You have ${properties.length} saved ${properties.length === 1 ? 'property' : 'properties'}`
              : 'Save properties you love to find them easily later'}
          </p>
        </div>

        {/* Favorites Grid */}
        {properties.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card key={property.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative aspect-[16/10]">
                  <img
                    src={property.images?.[0]}
                    alt={property.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Favorite Button */}
                  <div className="absolute top-3 right-3">
                    <FavoriteButton propertyId={property.id} />
                  </div>
                  {/* Price Badge */}
                  <div className="absolute bottom-3 left-3 bg-white px-3 py-1 rounded-full shadow">
                    <span className="font-bold text-slate-900">${property.price_per_night}</span>
                    <span className="text-xs text-slate-500">/night</span>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-1">{property.name}</h3>
                  <div className="flex items-center text-slate-500 text-sm mb-3">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{property.location}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-1">
                      <Bed className="h-4 w-4" />
                      <span>{property.bedrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath className="h-4 w-4" />
                      <span>{property.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{property.sleeps}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/property/${property.id}`} className="flex-1">
                      <Button variant="outline" className="w-full">
                        View Details
                      </Button>
                    </Link>
                    <a 
                      href={property.booking_url || '/properties'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">
                        Book Now
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg">
            <Heart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No favorites yet</h2>
            <p className="text-slate-600 mb-6">
              Start exploring properties and tap the heart icon to save your favorites.
            </p>
            <Link to="/properties">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                Browse Properties
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
